/**
 * Guests: someone who opens a world's link in a browser, without the app and without signing in,
 * gets a throwaway account from our server (a friendly name, a token for 24 hours), kept in this
 * browser. A guest plays like anyone, but can't create worlds and is never kept: the server forgets
 * them, pockets and all, when they leave. This wraps the Keycloak sign-in: the same Auth, with a
 * guest session while there is one. Signing in or up with Keycloak ends it.
 */
import { logger } from '@core/log';
import type { Storage } from '@platform/storage';

import { createGuest } from './api';
import { claimsOf, type Auth, type AuthState, type Identity } from './auth';

const log = logger('guest');
const GUEST_KEY = 'auth:guest';
/** Stop using a guest token this long before it runs out; the server's answer would be 401. */
const EXPIRY_MARGIN_MS = 60_000;
/** A guest token lasts this long when it doesn't say. */
const LIFETIME_MS = 24 * 3600_000;

interface GuestSession {
  token: string;
  identity: Identity;
  /** Wall-clock epoch milliseconds. */
  expiresAt: number;
}

export interface Players extends Auth {
  /** Become a guest to join `world` (its link was opened here); `lang` names them. */
  asGuest(world: string, lang: string): Promise<void>;
  /** Settles once start() has restored whoever was signed in here, if anyone. */
  started(): Promise<void>;
}

export function withGuests(
  keycloak: Auth,
  deps: { storage: Storage; now?: () => number },
): Players {
  const { storage } = deps;
  const now = deps.now ?? (() => Date.now()); // eslint-disable-line no-restricted-globals, no-restricted-syntax -- token lifetimes are wall-clock
  const listeners = new Set<(state: AuthState) => void>();
  let guest: GuestSession | null = null;
  let markStarted: () => void = () => undefined;
  const startedOnce = new Promise<void>((resolve) => {
    markStarted = resolve;
  });

  const state = (): AuthState =>
    guest ? { kind: 'in', identity: guest.identity } : keycloak.state();
  const tell = (): void => {
    const next = state();
    for (const listener of [...listeners]) listener(next);
  };
  const live = (session: GuestSession | null): session is GuestSession =>
    session !== null && session.expiresAt - EXPIRY_MARGIN_MS > now();
  const end = async (): Promise<void> => {
    guest = null;
    await storage.remove(GUEST_KEY);
  };

  // a Keycloak sign-in takes over from a guest
  keycloak.subscribe((next) => {
    if (next.kind === 'in' && guest) void end();
    if (!guest) tell();
  });

  return {
    state,
    identity: () => (guest ? guest.identity : keycloak.identity()),
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    async start() {
      const stored = await storage.get(GUEST_KEY);
      let session: GuestSession | null = null;
      try {
        session = stored ? (JSON.parse(stored) as GuestSession) : null;
      } catch {
        /* unreadable: no guest */
      }
      if (live(session)) {
        guest = session;
        tell();
      } else if (stored) await end();
      try {
        await keycloak.start();
      } finally {
        markStarted();
      }
    },
    started: () => startedOnce,
    login: (flow) => keycloak.login(flow),
    async logout() {
      if (!guest) return keycloak.logout();
      await end();
      tell();
    },
    async accessToken(fresh) {
      if (!guest) return keycloak.accessToken(fresh);
      if (live(guest)) return guest.token;
      log.info('the guest account ran out');
      await end();
      tell();
      return null;
    },
    async asGuest(world, lang) {
      const made = await createGuest(world, lang);
      const exp = claimsOf(made.token)?.['exp'];
      guest = {
        token: made.token,
        identity: { id: made.id, name: made.name, email: null },
        expiresAt: typeof exp === 'number' ? exp * 1000 : now() + LIFETIME_MS,
      };
      await storage.set(GUEST_KEY, JSON.stringify(guest));
      tell();
    },
  };
}
